import type { QueryClient } from '@tanstack/react-query'

import { syncCatalogue } from '../../api/catalogue'
import type { SyncResult } from '../../api/catalogue'
import { t } from '../../i18n'

/**
 * The channel synchronisation, held outside the screen that starts it.
 *
 * It used to be an async function inside the component. Leaving the screen --
 * to answer a client in the inbox, which is the whole reason the panel is open
 * -- did not stop the loop: it went on posting rounds for as long as forty of
 * them, while its progress and its stop flag belonged to a component that no
 * longer existed. Coming back showed a fresh «Синхронизировать» button, as if
 * nothing were happening, and pressing it started a second loop over the same
 * pending posts and the same covers: twice the model calls, twice the downloads
 * through the personal Telegram account, and no way to stop either short of
 * reloading the tab. That request rate is exactly what CLAUDE.md names as the
 * road to FLOOD_WAIT and a lost working account.
 *
 * So the run lives here, in the module, and the screen only subscribes to it.
 * Returning shows the progress that is really there, and a second press cannot
 * start anything while the first is going.
 */

/** How many rounds one press may run. Enough for a whole year of channel at
 * eight thumbnails and four readings a round, and a stop against a loop that
 * never converges. */
const MAX_ROUNDS = 40

/** How long one round may take before the screen calls it stuck. The server
 * gives itself sixty seconds; this is longer, so a slow round is reported by the
 * server's own error rather than guessed at here. */
const ROUND_TIMEOUT_MS = 75_000

export interface Run {
  readonly running: boolean
  readonly read: number
  readonly covers: number
  /** Undefined until the first round comes back, because "осталось: 0" before
   * anything has been counted says the job is finished when it has not started. */
  readonly left: number | undefined
  /** Why it ended badly, or what is still left after a stop. */
  readonly failure: string | null
  /** Set when a run reached the end of the queue. */
  readonly result: SyncResult | null
}

const idle: Run = {
  running: false,
  read: 0,
  covers: 0,
  left: undefined,
  failure: null,
  result: null,
}

let current: Run = idle
let stopped = false
const listeners = new Set<() => void>()

function set(next: Run): void {
  current = next
  for (const listener of listeners) listener()
}

/** The current state. A stable reference between changes, as
 * useSyncExternalStore requires. */
export function snapshot(): Run {
  return current
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Asks the run to stop after the round it is in. */
export function stopSync(): void {
  stopped = true
}

class TimeoutError extends Error {}

/** Fails a promise that takes too long, so the screen can say "слишком долго"
 * instead of spinning for ever. */
async function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const limit = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new TimeoutError('timed out'))
    }, ms)
  })

  try {
    return await Promise.race([work, limit])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message !== '' ? error.message : t('catalogue.syncFailed')
}

/**
 * Starts a run, unless one is already going.
 *
 * Pressing sync once should finish the job, not do a twelfth of it. So it runs
 * rounds: each call does a bounded chunk -- eight thumbnails and four readings
 * -- and says how much is left, and this keeps going until nothing is. The
 * bounded chunk is not an optimisation: one press used to be seventy-two
 * seconds of nothing and then no result at all, because the request ran past the
 * server's write timeout and the connection was cut.
 */
export function startSync(client: QueryClient): void {
  if (current.running) return
  stopped = false
  set({ ...idle, running: true })
  void run(client)
}

async function run(client: QueryClient): Promise<void> {
  let read = 0
  let covers = 0
  let byHand = false

  for (let round = 0; round < MAX_ROUNDS; round++) {
    let result: SyncResult
    try {
      result = await withTimeout(syncCatalogue(), ROUND_TIMEOUT_MS)
    } catch (error) {
      set({
        running: false,
        read,
        covers,
        left: undefined,
        result: null,
        failure: error instanceof TimeoutError ? t('catalogue.syncTimeout') : messageOf(error),
      })
      return
    }

    read += result.read
    covers += result.covers
    const left = result.pendingLeft + result.coversLeft
    set({ running: true, read, covers, left, failure: null, result: null })
    // The cache is the application's, not the screen's, so this is still right
    // when nobody is looking at the queue.
    void client.invalidateQueries({ queryKey: ['catalogue'] })

    if (left === 0 || (result.read === 0 && result.covers === 0)) {
      set({
        running: false,
        read,
        covers,
        left: 0,
        failure: null,
        result: { ...result, read, covers },
      })
      return
    }
    if (stopped) {
      byHand = true
      break
    }
  }

  // Out of rounds or stopped by hand: the work done is real and the rest is a
  // press away, so both are reported rather than swallowed.
  set({
    running: false,
    read,
    covers,
    left: undefined,
    result: null,
    failure: byHand ? t('catalogue.syncStopped') : t('catalogue.syncMoreLeft'),
  })
}
